import { api } from './client';
import type { Page } from './catalogs';

// ==================== Normalization ====================

export type ExpenseType = 'REPRESENTATION' | 'CHARITABLE' | 'NON_TARGETED_CHARITY';

export interface Normalization {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_quarter: number;
  expense_type: ExpenseType;
  actual_amount: string;
  limit_base_amount: string;
  limit_rate: string;
  limit_amount: string;
  non_deductible_amount: string;
  calculation_details: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface NormalizationCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  period_year: number;
  period_quarter: number;
  expense_type: ExpenseType;
  actual_amount?: string;
  limit_base_amount?: string;
  limit_rate: string;
  calculation_details?: Record<string, unknown> | null;
}

export interface CalculateNormResponse {
  expense_type: ExpenseType;
  limit_base_amount: string;
  limit_rate: string;
  limit_amount: string;
  suggestion: string;
}

const NORM_BASE = '/documents/income-tax-expense-normalization';

export const normalizations = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_quarter?: number;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<Normalization>>(NORM_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Normalization>(`${NORM_BASE}/${id}`).then((r) => r.data),
  create: (payload: NormalizationCreate) =>
    api.post<Normalization>(NORM_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Partial<NormalizationCreate>) =>
    api.patch<Normalization>(`${NORM_BASE}/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${NORM_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<Normalization>(`${NORM_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<Normalization>(`${NORM_BASE}/${id}/unpost`).then((r) => r.data),
  calculateNorm: (params: {
    organization_id: string;
    period_year: number;
    period_quarter: number;
    expense_type: ExpenseType;
  }) =>
    api
      .get<CalculateNormResponse>(`${NORM_BASE}/calculate-norm`, { params })
      .then((r) => r.data),
};

// ==================== Calculation ====================

export interface IncomeTaxCalculation {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  period_year: number;
  period_quarter: number;
  accounting_profit: string;
  normalizations_addback: string;
  other_addbacks: string;
  other_deductions: string;
  taxable_profit: string;
  tax_rate: string;
  tax_amount: string;
  advance_payments_made: string;
  tax_to_pay: string;
  calculation_details: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface IncomeTaxCalculationCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  period_year: number;
  period_quarter: number;
  accounting_profit?: string;
  normalizations_addback?: string;
  other_addbacks?: string;
  other_deductions?: string;
  tax_rate?: string;
  advance_payments_made?: string;
  calculation_details?: Record<string, unknown> | null;
}

export interface FetchProfit79Response {
  period_year: number;
  period_quarter: number;
  credit_total: string;
  debit_total: string;
  accounting_profit: string;
  entries_count: number;
}

export interface SumNormalizationsResponse {
  period_year: number;
  period_quarter: number;
  total_non_deductible: string;
  source_documents_count: number;
}

const CALC_BASE = '/documents/income-tax-calculation';

export const incomeTaxCalculations = {
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_quarter?: number;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<IncomeTaxCalculation>>(CALC_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<IncomeTaxCalculation>(`${CALC_BASE}/${id}`).then((r) => r.data),
  create: (payload: IncomeTaxCalculationCreate) =>
    api.post<IncomeTaxCalculation>(CALC_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Partial<IncomeTaxCalculationCreate>) =>
    api.patch<IncomeTaxCalculation>(`${CALC_BASE}/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${CALC_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<IncomeTaxCalculation>(`${CALC_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<IncomeTaxCalculation>(`${CALC_BASE}/${id}/unpost`).then((r) => r.data),
  fetchFromAccount79: (params: {
    organization_id: string;
    period_year: number;
    period_quarter: number;
  }) =>
    api
      .get<FetchProfit79Response>(`${CALC_BASE}/fetch-from-account-79`, { params })
      .then((r) => r.data),
  sumNormalizations: (params: {
    organization_id: string;
    period_year: number;
    period_quarter: number;
  }) =>
    api
      .get<SumNormalizationsResponse>(`${CALC_BASE}/sum-normalizations`, { params })
      .then((r) => r.data),
};
